import React, { createContext, useState, useEffect, useRef } from "react";
import api from "../api/axios";
import { unsubscribeFromPush } from "../utils/pushNotification";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const decodeJwt = (token) => {
    if (!token) return null;
    try {
      const payload = token.split(".")[1];
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const json = atob(base64);
      return JSON.parse(json);
    } catch (err) {
      return null;
    }
  };

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshRes = await api.post(
        "/auth/refresh-token",
        {},
        { withCredentials: true }
      );

      if (refreshRes.data?.success) {
        const newToken = refreshRes.data?.token;
        if (newToken) {
          localStorage.setItem("token", newToken);
          scheduleTokenRefresh(newToken);
        }
        return true;
      }
    } catch (err) {
      console.error("Session refresh failed:", err?.message || err);
    }

    clearAuthState();
    return false;
  };

  const scheduleTokenRefresh = (accessToken) => {
    clearRefreshTimer();

    const payload = decodeJwt(accessToken);
    if (!payload?.exp) return;

    const expiresAt = payload.exp * 1000;
    const now = Date.now();
    const refreshAt = Math.max(expiresAt - 2 * 60 * 1000, now + 5000);
    const delay = refreshAt - now;

    if (delay <= 0) {
      refreshAccessToken();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshAccessToken();
    }, delay);
  };

  const clearAuthState = () => {
    setUser(null);
    setVendorProfile(null);
    setAgentProfile(null);
    localStorage.removeItem("user");
    localStorage.removeItem("vendorProfile");
    localStorage.removeItem("agentProfile");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearRefreshTimer();
  };

  // Load from localStorage on app start and validate session
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const savedUser = localStorage.getItem("user");
        const savedVendorProfile = localStorage.getItem("vendorProfile");
        const savedAgentProfile = localStorage.getItem("agentProfile");

        if (!savedUser) {
          setLoading(false);
          return;
        }

        // Validate the token is still valid by calling /auth/me
        try {
          const res = await api.get("/auth/me");
          if (res.data?.success) {
            // Session is valid — restore user data
            const validatedUser = res.data.user;
            setUser(validatedUser);
            // Update localStorage with validated data
            localStorage.setItem("user", JSON.stringify(validatedUser));

            if (savedVendorProfile) {
              setVendorProfile(JSON.parse(savedVendorProfile));
            }

            if (savedAgentProfile) {
              setAgentProfile(JSON.parse(savedAgentProfile));
            }
          } else {
            // Session invalid — clean up
            throw new Error("Session invalid");
          }
        } catch {
          // Token expired or invalid — attempt refresh
          try {
            const refreshRes = await api.post("/auth/refresh-token", {}, { withCredentials: true });

            if (refreshRes.data?.success) {
              const newToken = refreshRes.data?.token;
              if (newToken) {
                localStorage.setItem("token", newToken);
                scheduleTokenRefresh(newToken);
              }
              // Fetch fresh user data to avoid stale localStorage (e.g. missing addresses)
              try {
                const meRes = await api.get("/auth/me");
                if (meRes.data?.success) {
                  setUser(meRes.data.user);
                  localStorage.setItem("user", JSON.stringify(meRes.data.user));
                } else {
                  setUser(JSON.parse(savedUser));
                }
              } catch {
                setUser(JSON.parse(savedUser));
              }
              if (savedVendorProfile) setVendorProfile(JSON.parse(savedVendorProfile));
              if (savedAgentProfile) setAgentProfile(JSON.parse(savedAgentProfile));
            } else {
              throw new Error("Refresh failed");
            }
          } catch {
            clearAuthState();
          }
        }
      } catch (e) {
        console.error("Failed to restore auth state:", e);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    restoreSession();

    return () => {
      clearRefreshTimer();
    };
  }, []);

  /**
   * Login
   */
  const login = (userObject, vendorProfileObject = null, agentProfileObject = null, accessToken = null) => {
    if (!userObject || typeof userObject !== "object") {
      console.error("Invalid user object:", userObject);
      return false;
    }

    setUser(userObject);
    localStorage.setItem("user", JSON.stringify(userObject));

    const tokenToUse = accessToken || localStorage.getItem("token");
    if (tokenToUse) {
      scheduleTokenRefresh(tokenToUse);
    }

    // Clear stale user-specific data on login
    localStorage.removeItem("appCart");
    localStorage.removeItem("recentlyViewed");
    window.dispatchEvent(new Event("user-login"));

    if (vendorProfileObject && userObject?.role === "vendor") {
      setVendorProfile(vendorProfileObject);
      localStorage.setItem(
        "vendorProfile",
        JSON.stringify(vendorProfileObject)
      );
    } else {
      setVendorProfile(null);
      localStorage.removeItem("vendorProfile");
    }

    if (agentProfileObject && userObject?.role === "agent") {
      setAgentProfile(agentProfileObject);
      localStorage.setItem(
        "agentProfile",
        JSON.stringify(agentProfileObject)
      );
    } else {
      setAgentProfile(null);
      localStorage.removeItem("agentProfile");
    }

    return true;
  };

  /**
   * Logout
   */
  const logout = async () => {
    // Unsubscribe this device from push notifications before clearing state
    await unsubscribeFromPush();

    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      // Ignore errors — still clear local state
    }
    clearRefreshTimer();
    setUser(null);
    setVendorProfile(null);
    setAgentProfile(null);
    localStorage.removeItem("user");
    localStorage.removeItem("vendorProfile");
    localStorage.removeItem("agentProfile");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("agentPushEnabled");
    localStorage.removeItem("appCart");
    localStorage.removeItem("recentlyViewed");
  };

  /**
   * Update user
   */
  const updateUser = (updatedData, replace = false) => {
    if (!user && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...user, ...updatedData };

    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));

    return true;
  };

  /**
   * Update vendor profile
   */
  const updateVendorProfile = (updatedData, replace = false) => {
    if (!vendorProfile && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...vendorProfile, ...updatedData };

    setVendorProfile(updated);
    localStorage.setItem(
      "vendorProfile",
      JSON.stringify(updated)
    );

    return true;
  };

  const updateAgentProfile = (updatedData, replace = false) => {
    if (!agentProfile && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...agentProfile, ...updatedData };

    setAgentProfile(updated);
    localStorage.setItem(
      "agentProfile",
      JSON.stringify(updated)
    );

    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        vendorProfile,
        agentProfile,
        login,
        logout,
        updateUser,
        updateVendorProfile,
        updateAgentProfile,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};